<script setup lang="ts">
/**
 * LeftPanel — Tab switching between Ship, Market, Public, Personal browsers
 *
 * Events:
 *   select-ship — Forwarded from ShipBrowser
 *   select-item — Forwarded from MarketBrowser
 *   select-fit  — Forwarded from FittingsBrowser/PersonalFittings
 */
import { useFitStore } from '../../stores/fitStore';
import ShipBrowser from '../browser/ShipBrowser.vue';
import MarketBrowser from '../browser/MarketBrowser.vue';
import FittingsBrowser from '../browser/FittingsBrowser.vue';
import PersonalFittings from '../browser/PersonalFittings.vue';

const store = useFitStore();

const emit = defineEmits<{
  'select-ship': [typeID: number, typeName: string];
  'select-item': [typeID: number, typeName: string];
  'select-fit': [fitID: number];
}>();

type TabDef = { key: 'ships' | 'market' | 'fittings' | 'personal'; label: string };
const tabs: TabDef[] = [
  { key: 'ships', label: 'Ships' },
  { key: 'market', label: 'Market' },
  { key: 'fittings', label: 'Public' },
  { key: 'personal', label: 'Personal' },
];
</script>

<template>
  <aside class="left-panel">
    <!-- Tab bar -->
    <div class="browser-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="tab-btn"
        :class="{ 'tab-btn--active': store.activeLeftTab === tab.key }"
        @click="store.setLeftTab(tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Tab content -->
    <div class="browser-content">
      <ShipBrowser
        v-if="store.activeLeftTab === 'ships'"
        @select-ship="(id: number, name: string) => emit('select-ship', id, name)"
      />
      <MarketBrowser
        v-else-if="store.activeLeftTab === 'market'"
        @select-item="(id: number, name: string) => emit('select-item', id, name)"
      />
      <FittingsBrowser
        v-else-if="store.activeLeftTab === 'fittings'"
      />
      <PersonalFittings
        v-else-if="store.activeLeftTab === 'personal'"
        @select-fit="(id: number) => emit('select-fit', id)"
      />
    </div>
  </aside>
</template>

<style scoped>
.left-panel {
  display: flex;
  flex-direction: column;
  width: 340px;
  min-width: 0;
  max-width: 100%;
  background:
    linear-gradient(180deg, rgba(17, 24, 32, 0.96), rgba(8, 11, 16, 0.98)),
    var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--glow-panel);
  overflow: hidden;
}

.browser-tabs {
  display: flex;
  flex-shrink: 0;
  gap: 2px;
  padding: 6px 6px 0;
  border-bottom: 1px solid var(--border-color);
  background: rgba(0, 0, 0, 0.12);
}

.tab-btn {
  flex: 1;
  padding: 8px 4px;
  border: 1px solid transparent;
  border-bottom: 2px solid transparent;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: center;
}

.tab-btn:hover {
  color: var(--text-primary);
  background: rgba(0, 212, 255, 0.055);
  border-color: rgba(0, 212, 255, 0.12);
}

.tab-btn--active {
  color: var(--accent-blue);
  border-color: var(--border-color);
  border-bottom-color: var(--accent-blue);
  background: linear-gradient(180deg, rgba(0, 212, 255, 0.11), rgba(0, 212, 255, 0.035));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.browser-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

@media (max-width: 900px) {
  .left-panel {
    width: 100%;
    min-height: 280px;
  }
}

@media (max-width: 600px) {
  .tab-btn {
    padding: 6px 2px;
    font-size: 0.62rem;
  }
}
</style>
